import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Search, Loader2, Download, ScrollText, ChevronDown, ChevronUp,
} from 'lucide-react';
import logger from '../../lib/logger';
import { ROLE_BADGES, formatDuration } from './_helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Impersonation Audit Log tab — extracted from AdminPage.js (v221).
 *
 * Self-managed state + fetch lifecycle:
 *  • Initial load on mount (only fires when this tab is mounted, which
 *    matches the prior behaviour of "fetch on first activation").
 *  • Search, pagination, and CSV/XLSX export are all internal.
 *
 * Super-admin gating is enforced by the parent route — AdminPage only
 * renders this when `isSuperAdmin && activeTab === 'audit-log'`.
 */
export default function AuditLogTab() {
  const [entries, setEntries] = useState([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const fetchAuditLog = useCallback(async (p, q) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/impersonate/audit-log`, {
        params: { page: p || page, limit: 20, search: q ?? search },
        withCredentials: true,
      });
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
      setPage(p || page);
    } catch (err) {
      logger.error('Audit log fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  // Initial fetch on mount.
  useEffect(() => {
    fetchAuditLog(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportAuditLog = async (format) => {
    try {
      const resp = await axios.get(`${API_URL}/api/admin/impersonate/audit-log/export`, {
        params: { format },
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `impersonation_audit_log.${format === 'xlsx' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      logger.error('Audit log export error:', err);
    }
  };

  return (
    <div className="space-y-4" data-testid="admin-audit-log">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-[#0F2040]">Impersonation Audit Log</h2>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchAuditLog(1, search); }}
              placeholder="Search by admin or user..."
              className="pl-8 text-xs h-8 w-56"
              data-testid="audit-search"
            />
          </div>
          <Button size="sm" variant="outline" onClick={() => fetchAuditLog(1, search)} className="h-8 text-xs" data-testid="audit-search-btn">
            <Search className="w-3 h-3 mr-1" /> Search
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportAuditLog('csv')} className="h-8 text-xs" data-testid="audit-export-csv">
            <Download className="w-3 h-3 mr-1" /> CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportAuditLog('xlsx')} className="h-8 text-xs" data-testid="audit-export-xlsx">
            <Download className="w-3 h-3 mr-1" /> Excel
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
        </div>
      ) : entries.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <ScrollText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500">No impersonation sessions recorded yet.</p>
            <p className="text-xs text-slate-400 mt-1">Audit entries are created when a Super Admin uses "View as" on a user.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Admin</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Impersonated User</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Role</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Started</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Duration</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-slate-500">Pages</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => {
                    const roleBadge = ROLE_BADGES[entry.target_role] || ROLE_BADGES.user;
                    const isExpanded = expanded === entry.audit_id;
                    return (
                      <React.Fragment key={entry.audit_id}>
                        <tr
                          className="border-b hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => setExpanded(isExpanded ? null : entry.audit_id)}
                          data-testid={`audit-row-${entry.audit_id}`}
                        >
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-[#0F2040] text-xs">{entry.admin_name}</div>
                            <div className="text-[10px] text-slate-400">{entry.admin_email}</div>
                          </td>
                          <td className="px-4 py-2.5">
                            <div className="font-medium text-[#0F2040] text-xs">{entry.target_name}</div>
                            <div className="text-[10px] text-slate-400">{entry.target_email}</div>
                          </td>
                          <td className="px-4 py-2.5">
                            <Badge className={`text-[10px] ${roleBadge.className}`}>{roleBadge.label}</Badge>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-600">
                            {entry.started_at ? new Date(entry.started_at).toLocaleString() : '—'}
                          </td>
                          <td className="px-4 py-2.5">
                            <span className={`text-xs font-medium ${entry.ended_at ? 'text-slate-600' : 'text-amber-600'}`}>
                              {formatDuration(entry.duration_seconds)}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-slate-500">
                            {(entry.pages_visited || []).length}
                          </td>
                          <td className="px-4 py-2.5">
                            {isExpanded
                              ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                              : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={7} className="bg-slate-50 px-6 py-3">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-1">Session Details</p>
                                  <div className="text-xs space-y-1 bg-white rounded p-3 border border-slate-100">
                                    <div className="flex justify-between"><span className="text-slate-400">Started</span><span>{entry.started_at ? new Date(entry.started_at).toLocaleString() : '—'}</span></div>
                                    <div className="flex justify-between"><span className="text-slate-400">Ended</span><span>{entry.ended_at ? new Date(entry.ended_at).toLocaleString() : <span className="text-amber-600 font-medium">Active</span>}</span></div>
                                    <div className="flex justify-between"><span className="text-slate-400">Duration</span><span className="font-medium">{formatDuration(entry.duration_seconds)}</span></div>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-xs font-medium text-slate-500 mb-1">Pages Visited ({(entry.pages_visited || []).length})</p>
                                  <div className="max-h-40 overflow-y-auto bg-white rounded p-3 border border-slate-100 space-y-1">
                                    {(entry.pages_visited || []).length === 0 ? (
                                      <p className="text-xs text-slate-400 italic">No pages recorded</p>
                                    ) : (
                                      (entry.pages_visited || []).map((pv, i) => (
                                        <div key={`${pv.path}-${pv.visited_at || i}`} className="flex justify-between text-xs">
                                          <span className="text-[#0F2040] font-mono truncate max-w-[200px]">{pv.path}</span>
                                          <span className="text-slate-400 whitespace-nowrap ml-2">{pv.visited_at ? new Date(pv.visited_at).toLocaleTimeString() : ''}</span>
                                        </div>
                                      ))
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {total > 20 && (
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => fetchAuditLog(page - 1)} className="h-7 text-xs">Previous</Button>
                <Button size="sm" variant="outline" disabled={page * 20 >= total} onClick={() => fetchAuditLog(page + 1)} className="h-7 text-xs">Next</Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
